import {
    APPSTATE,
    LAYOUTSTATE,
    ROOMSSTATE,
    SCREENSTATE,
    SETTINGSSTATE,
    SIDEBAR,
} from '..'
import { Size } from '../../../types/mfc'
import { ScreenState } from '../../../types/state'
import {
    maxVideoPercentage,
    minVideoPercentage,
} from '../../components/presentation/Chat/common/utilities'
import APP_BREAKPOINTS from '../../components/styles/breakpoints'
import {
    COMPONENT_HEIGHTS,
    COMPONENT_WIDTHS,
    EXPANDED_MOBILE_SEARCH_HEIGHT,
    getBrowserBottomInset,
    MIN_CONTENT_WIDTH_DESKTOP,
    MIN_CONTENT_WIDTH_MOBILE,
    PINNED_ROOM_CHAT_MIN_WIDTH,
    PINNED_ROOM_MAX_HEIGHT,
    PINNED_ROOM_MIN_WIDTH_FOR_CHAT,
} from '../../components/theme'
import { FloaterPageState, ViewMode } from '../../constants/enums'
import { isTouchDevice } from '../../utilities/browser'
import { getModalState, getScreenState } from '../getState'
import { pubSubScreen } from '../pubSub'
import { pinRoom, showPinRoomChat } from '../state.rooms'
import { updatePortalState } from './layout'

export const setScreenState = (value: Partial<ScreenState>) => {
    const changed = Object.keys(value).some(
        (key) => SCREENSTATE.screen[key] !== value[key],
    )
    if (!changed) return
    SCREENSTATE.screen = { ...SCREENSTATE.screen, ...value }
    pubSubScreen.emit('state', SCREENSTATE.screen, true)
}

export const getContentContainerWidth = ({
    vertical,
    width,
    opened,
    ratio,
    isMobileDevice,
}) => {
    // console.log('[getContentContainerWidth]', {
    //     vertical,
    //     width,
    //     opened,
    //     ratio,
    // })
    if (opened && !vertical) {
        return Math.min(
            Math.max(
                ratio * width,
                isMobileDevice
                    ? MIN_CONTENT_WIDTH_MOBILE
                    : MIN_CONTENT_WIDTH_DESKTOP,
            ),
            width,
        )
    }
    return width
}

export const getContentContainerHeight = ({
    opened,
    // passiveMode,
    vertical,
    height,
    pinContainerHeight,
}) => {
    if (opened) {
        if (vertical) {
            return height - pinContainerHeight
        }
    }
    return height
}

export const getPinLimit = ({ singlePin, isMobileDevice, isVertical }) => {
    // mobile
    if (singlePin) {
        return 1
    }
    // tablet
    if (isTouchDevice && !isMobileDevice) {
        return 2
    }
    // desktop
    if (isVertical) {
        return 2
    }
    return 99
}

export const getEstimatedPinnedRoomWidth = ({
    width,
    vertical,
    singlePin,
    showChat,
    pinCount,
}) => {
    if (vertical) {
        if (pinCount > 1) return width / 2
        if (showChat) {
            return width - PINNED_ROOM_CHAT_MIN_WIDTH
        }
        if (singlePin) return width
        return width
    }
    if (showChat) {
        return width - PINNED_ROOM_CHAT_MIN_WIDTH
    }
    return width
}
export const getShowChat = ({
    isVertical,
    isMobileDevice,
    isTabletDevice,
    pinContainerWidth,
    pinnedRooms,
}) => {
    if (isVertical && pinnedRooms.length === 1) {
        // console.log('[getShowChat]', pinnedRooms)
        return ROOMSSTATE.rooms[pinnedRooms[0]]?.showChat
    }
    if (isVertical && isTabletDevice && pinnedRooms.length > 1) return false
    if (!isVertical && pinContainerWidth < PINNED_ROOM_MIN_WIDTH_FOR_CHAT)
        return false
    const showChat = isVertical
        ? pinnedRooms.length < 2 && !isMobileDevice
        : !isMobileDevice && pinContainerWidth > 640
    const showTabletChat =
        (isVertical && isTabletDevice && pinnedRooms.length < 2) ||
        (!isVertical && showChat)

    return isTabletDevice ? showTabletChat : showChat
}

export const calculateSizes = ({ height, width, ratio }) => {
    const { pinnedRooms } = ROOMSSTATE
    const pinnedCount = pinnedRooms.length
    const opened = pinnedCount > 0 // && !passiveMode
    const isVertical = height > width
    // NOTE: `isVertical` is the general shape of the window, so calculatePortraitSize can infact
    // produce roomWidth / roomHeight values that would be used in a landscape room layout
    const { width: roomWidth, height: roomHeight } = isVertical
        ? calculatePortraitSize(height, width)
        : calculateLandscapeSize(height, width)
    const isMobileDevice = APPSTATE.app.isMobile

    const contentContainerWidth = getContentContainerWidth({
        vertical: isVertical,
        opened,
        width: roomWidth,
        ratio,
        isMobileDevice,
    })
    const pinContainerWidth = isVertical
        ? roomWidth
        : roomWidth - contentContainerWidth

    const isTabletDevice = !isMobileDevice && isTouchDevice
    const singlePin =
        isMobileDevice || (isVertical && !(width > APP_BREAKPOINTS.lg / 2))

    const showChat = getShowChat({
        isVertical,
        isMobileDevice,
        isTabletDevice,
        pinContainerWidth,
        pinnedRooms,
    })

    const pinLimit = getPinLimit({
        singlePin,
        isMobileDevice,
        isVertical,
        // width,
    })

    const estimatedVideoWidth = getEstimatedPinnedRoomWidth({
        width: pinContainerWidth,
        vertical: isVertical,
        singlePin,
        showChat,
        pinCount: pinnedCount,
    })

    // Mirror PinnedPortal semantics:
    // cap the video height (halve cap on mobile), then add chatHeader
    const videoHeightCap = isMobileDevice
        ? PINNED_ROOM_MAX_HEIGHT / 2
        : PINNED_ROOM_MAX_HEIGHT
    const videoHeightPortrait = Math.min(
        (estimatedVideoWidth * 9) / 16,
        videoHeightCap,
    )
    const pinContainerHeight = isVertical
        ? videoHeightPortrait + COMPONENT_HEIGHTS.chatHeader
        : roomHeight

    const contentContainerHeight = getContentContainerHeight({
        height: roomHeight,
        opened,
        vertical: isVertical,
        pinContainerHeight,
    })
    // console.log(
    //     '[calculateSizes]',
    //     { showChat, estimatedVideoWidth },
    //     {
    //         // showTabletChat,
    //         roomWidth,
    //         usableHeight,
    //         pinContainerWidth,
    //         pinContainerHeight,
    //     },
    //     { contentContainerWidth, contentContainerHeight },
    // )
    return {
        contentWidth: contentContainerWidth,
        contentHeight: contentContainerHeight,
        pinsWidth: pinContainerWidth,
        pinsHeight: pinContainerHeight,
        roomWidth,
        roomHeight,
        singlePin,
        pinLimit,
        showChat,
    }
}
export const checkPinnedRoomChat = (defaultShowChat: boolean) => {
    const { pinnedRooms } = ROOMSSTATE
    pinnedRooms.forEach((roomId) => {
        const room = ROOMSSTATE.rooms[roomId]
        if (!room) return
        if (room.showChat !== defaultShowChat) {
            showPinRoomChat(roomId, defaultShowChat)
        }
    })
}

export const checkPinnedRoomLimit = (pinnedCount: number, pinLimit: number) => {
    if (pinnedCount <= pinLimit) return false
    // unpinning recalculates the screen, which can apply the limit first
    ROOMSSTATE.pinnedRooms
        .slice(0, pinnedCount - pinLimit)
        .forEach((roomId) => {
            if (
                ROOMSSTATE.pinnedRooms.length > pinLimit &&
                ROOMSSTATE.pinnedRooms.includes(roomId)
            )
                pinRoom(roomId, { pinned: false })
        })
    return true
}

export const getOrientationState = ({
    windowWidth,
    almostSquare,
    isMobile,
    portrait,
    expandedNav,
    fullWindow,
    sidebarOpen,
    expanded,
}) => {
    if (fullWindow) return FloaterPageState.FULL_WINDOW
    if (!expanded) {
        if (portrait) {
            const smallViewport =
                windowWidth > 0 ? windowWidth < APP_BREAKPOINTS.md : isMobile
            if (!smallViewport && sidebarOpen)
                return FloaterPageState.PORTRAIT_EXPANDED_SIDEBAR_CONTENT
            return FloaterPageState.PORTRAIT_CONTENT
        }
        if (sidebarOpen) {
            if (expandedNav)
                return FloaterPageState.LANDSCAPE_EXPANDED_ALL_CONTENT
            return FloaterPageState.LANDSCAPE_EXPANDED_SIDEBAR_CONTENT
        }
        if (expandedNav) return FloaterPageState.LANDSCAPE_EXPANDED_NAV_CONTENT
        return FloaterPageState.LANDSCAPE_CONTENT
    }
    if (portrait) {
        const smallViewport =
            windowWidth > 0 ? windowWidth < APP_BREAKPOINTS.md : isMobile
        if (!smallViewport) {
            if (sidebarOpen) return FloaterPageState.PORTRAIT_EXPANDED_SIDEBAR
            return FloaterPageState.PORTRAIT_LANDSCAPE_MEDIAN
        }
        if (almostSquare) {
            return FloaterPageState.PORTRAIT_LANDSCAPE_MEDIAN
        }
        return FloaterPageState.PORTRAIT
    }
    if (sidebarOpen) {
        if (expandedNav) return FloaterPageState.LANDSCAPE_EXPANDED_ALL
        return FloaterPageState.LANDSCAPE_EXPANDED_SIDEBAR
    }
    if (expandedNav) return FloaterPageState.LANDSCAPE_EXPANDED_NAV
    return FloaterPageState.LANDSCAPE
}

export const getFullScreenRoomCoords = ({ floaterPortrait, windowWidth }) => {
    if (getModalState().floating_room.viewMode === ViewMode.FullWindow) {
        return {
            x: 0,
            y: 0,
        }
    }
    const headerHeight =
        windowWidth < APP_BREAKPOINTS.md
            ? COMPONENT_HEIGHTS.headerCompact
            : COMPONENT_HEIGHTS.headerDesktop
    if (floaterPortrait) {
        return {
            x: 0,
            y: LAYOUTSTATE.layout.expandedSearchMobile
                ? headerHeight + EXPANDED_MOBILE_SEARCH_HEIGHT
                : headerHeight,
        }
    } else {
        const navWidth = LAYOUTSTATE.layout.expandedNav
            ? COMPONENT_WIDTHS.leftNavExpanded
            : COMPONENT_WIDTHS.leftNav

        return {
            x: navWidth,
            y: LAYOUTSTATE.layout.expandedSearchMobile
                ? headerHeight + EXPANDED_MOBILE_SEARCH_HEIGHT
                : headerHeight,
        }
    }
}

export const calculateLandscapeSize = (
    windowHeight: number,
    windowWidth: number,
): Size => {
    const expandedNav = LAYOUTSTATE.layout.expandedNav
    const expandedSearchMobile = LAYOUTSTATE.layout.expandedSearchMobile
    const sidebarOpen = SIDEBAR.state.show
    const fullWindow =
        getModalState().floating_room.viewMode === ViewMode.FullWindow
    const navWidth = expandedNav
        ? COMPONENT_WIDTHS.leftNavExpanded
        : COMPONENT_WIDTHS.leftNav
    const width = Math.max(
        windowWidth -
            (fullWindow
                ? 0
                : navWidth + (sidebarOpen ? COMPONENT_WIDTHS.sidebar : 0)),
        0,
    )
    const headerHeight =
        windowWidth < APP_BREAKPOINTS.md
            ? COMPONENT_HEIGHTS.headerCompact
            : COMPONENT_HEIGHTS.headerDesktop
    const height = Math.max(
        windowHeight -
            (fullWindow
                ? 0
                : expandedSearchMobile
                  ? headerHeight + EXPANDED_MOBILE_SEARCH_HEIGHT
                  : headerHeight),
        0,
    )
    return { height, width }
}

export const applyLockedVideoDivision = (width: number) => {
    if (!SETTINGSSTATE.ROOM.videoSizeLocked) return
    const currentDivision = LAYOUTSTATE.portal.division
    const currentWidth = getScreenState().roomWidth
    const currentVideoWidth = currentWidth * (currentDivision / 100)
    const newDivision = (currentVideoWidth * 100) / width
    // ensure we maintain minimum chat size even when locked
    const maxDivision = maxVideoPercentage(width)
    const totalDivision =
        minVideoPercentage(width) + Math.min(newDivision, maxDivision)
    if (totalDivision > 101) {
        // when there isnt room for both to have their preferred dimension, ensure that they
        // share it more equally by scaling the chat back a bit
        updatePortalState({
            division:
                (100 / totalDivision) * Math.min(newDivision, maxDivision),
        })
    } else {
        updatePortalState({
            division: Math.min(newDivision, maxDivision),
        })
    }
}

export const calculatePortraitSize = (
    windowHeight: number,
    windowWidth: number,
): Size => {
    const expandedSearchMobile = LAYOUTSTATE.layout.expandedSearchMobile
    const sidebarOpen = SIDEBAR.state.show
    const width = Math.max(
        windowWidth - (sidebarOpen ? COMPONENT_WIDTHS.sidebar : 0),
        0,
    )

    const headerHeight =
        windowWidth < APP_BREAKPOINTS.md
            ? COMPONENT_HEIGHTS.headerCompact
            : COMPONENT_HEIGHTS.headerDesktop
    const composeFocused =
        LAYOUTSTATE.portal.composeFocused && !LAYOUTSTATE.portal.quietFocus
    const bottomInset = composeFocused ? 0 : getBrowserBottomInset()
    const height = Math.max(
        windowHeight -
            (COMPONENT_HEIGHTS.navBottom + bottomInset) -
            (expandedSearchMobile
                ? headerHeight + EXPANDED_MOBILE_SEARCH_HEIGHT
                : headerHeight),
        0,
    )

    return { width, height }
}
